import * as React from 'react'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { Link, useRouter } from '@tanstack/react-router'
import { useInterval } from 'ahooks'
import { useFeatureFlagEnabled } from 'posthog-js/react'

import { dayjs } from '@/lib/dayjs.ts'
import { cn } from '@/lib/utils.ts'
import { useBreadcrumbs } from '@/hooks/use-breadcrumbs.tsx'
import { useAppUpdateStore } from '@/hooks/use-update-app.ts'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Command } from '@/components/ui/command.tsx'
import { Separator } from '@/components/ui/separator'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar'
import { Toaster } from '@/components/ui/sonner'
import { useMenu } from '@/components/layout/use-menu.tsx'
import { PortalHost } from '@/components/portal/portal-host.tsx'
import { UpdateBanner } from '@/components/update-banner.tsx'
import { UserDropdown } from '@/components/user-dropdown.tsx'

const adminRoutes = ['/settings/attributes', '/settings/attribute-groups']
const isProd = import.meta.env.VITE_ENV === 'prod'

export function LoggedInLayout({ children }: { children: React.ReactNode }) {
  const { menu: fullMenu } = useMenu()
  const isAdmin = useFeatureFlagEnabled('is-admin')
  const menu = useMemo(() => {
    return fullMenu.map((menuGroup) => ({
      ...menuGroup,
      items: menuGroup.items.filter(
        (item) => !isProd || isAdmin || !adminRoutes.includes(item.to)
      ),
    }))
  }, [fullMenu, isAdmin])
  const { breadcrumbs } = useBreadcrumbs()

  return (
    <>
      <SidebarProvider>
        <SidebarNavigationTracker />
        <Sidebar>
          <SidebarHeader>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton size="lg" asChild>
                  <div>
                    <Link
                      to="/"
                      className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg"
                    >
                      <Command className="size-4" />
                    </Link>
                    <Link
                      to="/"
                      className="grid flex-1 text-left text-sm leading-tight"
                    >
                      <span className="truncate font-semibold">Ocarina</span>
                      {/*<span className="truncate text-xs">Enterprise</span>*/}
                    </Link>
                  </div>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarHeader>
          <SidebarContent className="flex h-svh overflow-hidden">
            {/* We create a SidebarGroup for each parent. */}
            {menu.map((item, index) => (
              <SidebarGroup key={index}>
                <SidebarGroupLabel>{item.title}</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {item.items.map((item, index) => (
                      <Link to={item.to} key={index}>
                        {({ isActive }) => (
                          <SidebarMenuItem key={item.title}>
                            <SidebarMenuButton isActive={isActive} asChild>
                              <div>
                                {item.icon ? <item.icon /> : null}
                                <span>{item.title}</span>
                              </div>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        )}
                      </Link>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            ))}
          </SidebarContent>
          <SidebarRail />
          <SidebarFooter>
            <UserDropdown />
          </SidebarFooter>
        </Sidebar>
        <SidebarInset className="flex h-svh gap-0 overflow-hidden">
          <header className="flex h-16 shrink-0 items-center justify-between gap-2 border-b px-4">
            <div className="flex items-center">
              <SidebarTrigger className="-ml-1" />
              <Separator orientation="vertical" className="mr-2 h-4" />
              <Breadcrumb>
                <BreadcrumbList className="flex-nowrap">
                  {breadcrumbs?.map((breadcrumb, index) => {
                    const isLast = index === breadcrumbs.length - 1
                    const hasNoLink = !breadcrumb.to

                    return (
                      <Fragment key={index}>
                        <BreadcrumbItem
                          className={cn(!isLast && 'hidden md:block')}
                        >
                          {isLast || hasNoLink ? (
                            <BreadcrumbPage className="line-clamp-3">
                              {breadcrumb.name}
                            </BreadcrumbPage>
                          ) : (
                            <BreadcrumbLink asChild>
                              <Link className="line-clamp-3" to={breadcrumb.to}>
                                {breadcrumb.name}
                              </Link>
                            </BreadcrumbLink>
                          )}
                        </BreadcrumbItem>
                        {!isLast && (
                          <BreadcrumbSeparator className="hidden md:block" />
                        )}
                      </Fragment>
                    )
                  })}
                </BreadcrumbList>
              </Breadcrumb>
            </div>

            <PortalHost id="header-actions" />
          </header>
          <div className="flex h-full flex-1 grow flex-col gap-4 overflow-hidden">
            {/*<div className="grid auto-rows-min gap-4 md:grid-cols-3">*/}
            {/*  <div className="aspect-video rounded-xl bg-muted/50" />*/}
            {/*  <div className="aspect-video rounded-xl bg-muted/50" />*/}
            {/*  <div className="aspect-video rounded-xl bg-muted/50" />*/}
            {/*</div>*/}
            {/*<div className="min-h-screen flex-1 rounded-xl bg-muted/50 md:min-h-min" />*/}
            <div className="size-full">{children}</div>
          </div>
        </SidebarInset>
      </SidebarProvider>
      <Toaster className="shadow-2xl" position="top-center" richColors={true} />

      <SmartUpdateBanner />
    </>
  )
}

const _30_MINUTES_IN_SECONDS = 60 * 30

function SmartUpdateBanner() {
  const hadUpdateAt = useAppUpdateStore((state) => state.hadUpdateAt)
  const [isBannerVisible, setIsBannerVisible] = useState(false)

  useInterval(() => {
    if (
      hadUpdateAt &&
      dayjs().diff(dayjs(hadUpdateAt), 'seconds') > _30_MINUTES_IN_SECONDS
    ) {
      return setIsBannerVisible(true)
    }

    setIsBannerVisible(false)
  }, 1000)

  if (isBannerVisible)
    return <UpdateBanner onUpdate={() => window.location.reload()} />

  return null
}

function SidebarNavigationTracker() {
  const sidebar = useSidebar()
  const router = useRouter()

  useEffect(() => {
    function onChangeRoute() {
      if (sidebar.isMobile) {
        sidebar.setOpenMobile(false)
      }
    }

    router.history.subscribe(onChangeRoute)
  }, [sidebar.isMobile])

  return null
}
